import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Cpu, User } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

export default function Header() {
  const loc = useLocation();
  const { user, logout } = useAuth();
  const isApiKey = loc.pathname === "/" && loc.hash === "#api-key";
  const [showProfileMenu, setShowProfileMenu] = React.useState(false);

  return (
    <header className="sticky top-0 z-50 border-b border-line-1 bg-void/70 backdrop-blur-xl">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-14 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2 group">
          <div className="relative w-7 h-7 border border-cyber/60 flex items-center justify-center">
            <Cpu size={14} className="text-cyber" strokeWidth={1.5} />
            <span className="absolute -top-1 -right-1 w-1.5 h-1.5 bg-terminal animate-pulse" />
          </div>
          <span className="font-display font-black tracking-tight text-base">
            Mnemonic<span className="text-brand">AI</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-7 text-xs tracking-[0.2em] uppercase">
          <a href="/#problem" data-testid="nav-problem" className="text-ink-500 hover:text-ink-100">
            Problem
          </a>
          <a href="/#how" data-testid="nav-how" className="text-ink-500 hover:text-ink-100">
            How it works
          </a>
          <a href="/#sdk" data-testid="nav-sdk" className="text-ink-500 hover:text-ink-100">
            SDK
          </a>
          <a href="/#pricing" data-testid="nav-pricing" className="text-ink-500 hover:text-ink-100">
            Pricing
          </a>
          <a
            href="/#api-key"
            data-testid="nav-apikey"
            className={`${isApiKey ? "text-cyber" : "text-ink-500 hover:text-ink-100"}`}
          >
            API Key
          </a>
        </nav>

        <div className="flex items-center gap-2">
          <a
            href="https://github.com/Nirvana3339/mnemonic-sdks"
            target="_blank"
            rel="noreferrer"
            data-testid="header-github"
            className="hidden sm:inline text-[11px] tracking-[0.2em] uppercase text-ink-500 hover:text-ink-100 px-3 py-1.5 border border-line-1 hover:border-line-2"
          >
            github
          </a>
          
          {user ? (
            // Logged in - show profile icon
            <div className="relative">
              <button
                onClick={() => setShowProfileMenu(!showProfileMenu)}
                className="flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-ink-100 px-3 py-1.5 border border-line-2 hover:border-cyber transition-colors"
              >
                <User size={14} />
                {user.email?.split('@')[0]}
              </button>
              
              {showProfileMenu && (
                <div className="absolute right-0 mt-2 w-48 border border-line-2 bg-surface shadow-lg">
                  <Link
                    to="/dashboard"
                    className="block px-4 py-2 text-sm text-ink-300 hover:text-ink-100 hover:bg-void"
                    onClick={() => setShowProfileMenu(false)}
                  >
                    Dashboard
                  </Link>
                  <button
                    onClick={() => {
                      logout();
                      setShowProfileMenu(false);
                    }}
                    className="block w-full text-left px-4 py-2 text-sm text-ink-300 hover:text-ink-100 hover:bg-void"
                  >
                    Logout
                  </button>
                </div>
              )}
            </div>
          ) : (
            // Not logged in - show Get Started and Login
            <>
              <Link
                to="/signup"
                data-testid="header-signup"
                className="text-[11px] tracking-[0.2em] uppercase text-ink-500 hover:text-ink-100 px-3 py-1.5"
              >
                Get Started
              </Link>
              <Link
                to="/login"
                data-testid="header-login"
                className="text-[11px] tracking-[0.2em] uppercase font-bold bg-cyber text-void px-4 py-1.5 hover:bg-cyber-deep transition-colors"
              >
                Login
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
